import React, { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaBook, FaVideo } from "react-icons/fa";
import { MdDeleteOutline, MdShoppingCartCheckout } from "react-icons/md";
import PriceDisplay from "./PriceDisplay";
import { getFinalPrice } from "../utils/pricing";

export default function CatalogueCartPanel({
  items,
  currency,
  balance,
  loadingBalance = false,
  onRemove,
  onCheckout,
  isDarkMode,
  loading,
}) {
  const total = items.reduce((sum, item) => sum + getFinalPrice(item), 0);
  const remaining = balance - total;
  const cartListRef = useRef(null);
  const previousItemCount = useRef(items.length);
  const icons = {
    video: FaVideo,
    material: FaBook,
  };

  useEffect(() => {
    if (items.length > previousItemCount.current) {
      requestAnimationFrame(() => {
        cartListRef.current?.scrollTo({
          top: cartListRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    }
    previousItemCount.current = items.length;
  }, [items.length]);

  return (
    <aside
      className={`flex h-[660px] flex-col overflow-hidden rounded-2xl border p-5 shadow-sm lg:sticky lg:top-2 ${
        isDarkMode
          ? "border-gray-700 bg-[#252525] text-white"
          : "border-gray-200 bg-white text-gray-900"
      }`}
    >
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">My Cart</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            {items.length > 0
              ? "Review your learning bundle"
              : "Build your learning bundle"}
          </p>
        </div>
        <span className="flex h-9 min-w-9 items-center justify-center rounded-full bg-blue-100 px-2 text-sm font-bold text-blue-700">
          {items.length}
        </span>
      </div>

      <div
        ref={cartListRef}
        className="
          min-h-0 flex-1 space-y-1
          overflow-x-hidden overflow-y-auto
          pr-1 scroll-smooth

          [scrollbar-width:thin]
          [scrollbar-color:#94a3b8_transparent]

          [&::-webkit-scrollbar]:w-[9px]
          [&::-webkit-scrollbar]:h-[9px]

          [&::-webkit-scrollbar-track]:bg-transparent
          [&::-webkit-scrollbar-track]:[margin-block:0.65rem]

          [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-thumb]:border-2
          [&::-webkit-scrollbar-thumb]:border-transparent
          [&::-webkit-scrollbar-thumb]:bg-slate-400
          [&::-webkit-scrollbar-thumb]:[background-clip:padding-box]

          [&::-webkit-scrollbar-thumb:hover]:bg-slate-500
          [&::-webkit-scrollbar-corner]:bg-transparent
        "
      >
        {items.length === 0 ? (
          <div className="flex min-h-40 flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 px-4 text-center">
            <MdShoppingCartCheckout className="mb-2 text-3xl text-gray-300" />
            <p className="text-sm font-medium text-gray-500">
              Your cart is empty
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Add content from the table
            </p>
          </div>
        ) : (
          <AnimatePresence initial={false} mode="popLayout">
            {items.map((item) => {
              const Icon = icons[item.type] || FaBook;
              return (
                <motion.div
                  layout
                  initial={{ opacity: 0, x: 28, scale: 0.96 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 28, scale: 0.96 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  key={`${item.id}-${item.type}`}
                  className={`group flex items-start gap-3 border-b py-3 ${
                    isDarkMode ? "border-gray-700" : "border-gray-100"
                  }`}
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <Icon />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-sm font-semibold"
                      title={item.title}
                    >
                      {item.title}
                    </p>
                    {item.description && (
                      <p
                        className="mt-0.5 line-clamp-2 text-xs leading-4 text-gray-500"
                        title={item.description}
                      >
                        {item.description}
                      </p>
                    )}
                    <PriceDisplay
                      item={item}
                      currency={currency}
                      className="mt-1 text-xs"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => onRemove(item.id, item.type)}
                    className="rounded-lg p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600"
                    aria-label={`Remove ${item.title}`}
                  >
                    <MdDeleteOutline size={19} />
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>

      <div
        className={`mt-5 space-y-3 border-t pt-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
      >
        <div className="flex justify-between text-sm text-gray-500">
          <span>Balance</span>
          <span>
            {loadingBalance
              ? "Checking?"
              : balance === null
                ? "Unavailable"
                : Number(balance).toFixed(2) + " " + currency}
          </span>
        </div>
        <div className="flex items-center justify-between text-lg font-bold">
          <span>Total</span>
          <span>
            {total.toFixed(2)} {currency}
          </span>
        </div>
        {!loadingBalance &&
          balance !== null &&
          items.length > 0 &&
          remaining < 0 && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
              You need {Math.abs(remaining).toFixed(2)} more {currency}.
            </p>
          )}
        <button
          type="button"
          onClick={onCheckout}
          disabled={!items.length || loading || loadingBalance}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#06315B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#0b477f] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <MdShoppingCartCheckout size={19} />
          {loading
            ? "Processing..."
            : loadingBalance
              ? "Checking wallet?"
              : balance === null
                ? "Retry wallet check"
                : "Create my course"}
        </button>
      </div>
    </aside>
  );
}
